import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
  Chip,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Tab,
  Tabs,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Mail as MailIcon,
  Drafts as DraftsIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { emailsService, EmailLog, EmailTemplate, SendEmailData } from '../services/emailsService';

const EmailsPage: React.FC = () => {
  const theme = useTheme();
  
  // State
  const [emails, setEmails] = useState<EmailLog[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [totalEmails, setTotalEmails] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Dialog states
  const [composeOpen, setComposeOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Compose form state
  const [composeData, setComposeData] = useState<SendEmailData>({
    to: '',
    subject: '',
    htmlBody: '',
    textBody: '',
  });
  
  // Notification state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch emails
  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await emailsService.getEmails({
        page,
        limit: 20,
        search: searchText || undefined,
        status: statusFilter || undefined,
      });
      setEmails(response.data);
      setTotalEmails(response.pagination.total);
    } catch (error) {
      console.error('Failed to fetch emails:', error);
      setSnackbar({ open: true, message: 'Failed to load emails', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, searchText, statusFilter]);

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const data = await emailsService.getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  useEffect(() => {
    fetchEmails();
    fetchTemplates();
  }, [fetchEmails]);

  // Handle compose changes
  const handleComposeChange = (field: keyof SendEmailData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setComposeData({ ...composeData, [field]: event.target.value });
  };

  // Apply template
  const handleApplyTemplate = (template: EmailTemplate) => {
    setComposeData({
      ...composeData,
      subject: template.subject,
      htmlBody: `<p>Dear {{client_name}},</p><p>Your content here...</p><p>Best regards,<br/>REDEX Team</p>`,
    });
  };

  // Send email
  const handleSendEmail = async () => {
    setSending(true);
    try {
      await emailsService.sendEmail(composeData);
      setSnackbar({ open: true, message: 'Email sent successfully', severity: 'success' });
      setComposeOpen(false);
      setComposeData({ to: '', subject: '', htmlBody: '', textBody: '' });
      fetchEmails();
    } catch (error) {
      console.error('Failed to send email:', error);
      setSnackbar({ open: true, message: 'Failed to send email', severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  // View email
  const handleViewEmail = (email: EmailLog) => {
    setSelectedEmail(email);
    setViewOpen(true);
  };

  // Get status color and icon
  const getStatusInfo = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'sent':
      case 'delivered':
        return { color: theme.palette.success.main, icon: <CheckCircleIcon fontSize="small" /> };
      case 'failed':
      case 'bounced':
        return { color: theme.palette.error.main, icon: <ErrorIcon fontSize="small" /> };
      case 'pending':
        return { color: theme.palette.warning.main, icon: <ScheduleIcon fontSize="small" /> };
      default:
        return { color: theme.palette.grey[500], icon: <MailIcon fontSize="small" /> };
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    if (diff < 86400000) { // Less than 24 hours
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) { // Less than 7 days
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Get initial for avatar
  const getInitial = (email: string) => {
    return email.charAt(0).toUpperCase();
  };

  // Get avatar color based on email
  const getAvatarColor = (email: string) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.info.main,
    ];
    const index = email.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Count emails by status
  const countByStatus = (status: string) => {
    return emails.filter(e => e.status?.toLowerCase() === status).length;
  };

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 100px)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Email
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {totalEmails} emails in your history
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchEmails}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setComposeOpen(true)}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            Compose
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', height: 'calc(100% - 80px)', gap: 3 }}>
        {/* Sidebar */}
        <Paper
          sx={{
            width: 250,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            display: { xs: 'none', md: 'block' },
            overflow: 'hidden',
          }}
        >
          <Tabs
            orientation="vertical"
            value={selectedTab}
            onChange={(_, v) => setSelectedTab(v)}
            sx={{ borderRight: 1, borderColor: 'divider' }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <MailIcon />
                  <Typography>All Emails</Typography>
                  <Badge badgeContent={totalEmails} color="primary" sx={{ ml: 'auto' }} />
                </Box>
              }
              sx={{ justifyContent: 'flex-start', px: 2 }}
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <CheckCircleIcon />
                  <Typography>Sent</Typography>
                  <Badge badgeContent={countByStatus('sent')} color="success" sx={{ ml: 'auto' }} />
                </Box>
              }
              sx={{ justifyContent: 'flex-start', px: 2 }}
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <ErrorIcon />
                  <Typography>Failed</Typography>
                  <Badge badgeContent={countByStatus('failed')} color="error" sx={{ ml: 'auto' }} />
                </Box>
              }
              sx={{ justifyContent: 'flex-start', px: 2 }}
            />
          </Tabs>

          <Divider />

          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Quick Templates
            </Typography>
            <List dense>
              {templates.slice(0, 4).map((template) => (
                <ListItem
                  button
                  key={template.id}
                  onClick={() => {
                    handleApplyTemplate(template);
                    setComposeOpen(true);
                  }}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemText
                    primary={template.name}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Paper>

        {/* Email List */}
        <Paper
          sx={{
            flex: 1,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Search */}
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search emails..."
                  size="small"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="sent">Sent</MenuItem>
                    <MenuItem value="delivered">Delivered</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="bounced">Bounced</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Email List */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : emails.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, flexDirection: 'column' }}>
              <MailIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">No emails found</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setComposeOpen(true)}
                sx={{ mt: 2 }}
              >
                Send your first email
              </Button>
            </Box>
          ) : (
            <List sx={{ overflow: 'auto', flex: 1, p: 0 }}>
              {emails.map((email) => {
                const statusInfo = getStatusInfo(email.status);
                return (
                  <React.Fragment key={email.id}>
                    <ListItem
                      alignItems="flex-start"
                      button
                      onClick={() => handleViewEmail(email)}
                      sx={{
                        '&:hover': {
                          bgcolor: alpha(theme.palette.action.hover, 0.1),
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: getAvatarColor(email.to_email) }}>
                          {getInitial(email.to_email)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                              {email.to_email}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                icon={statusInfo.icon}
                                label={email.status}
                                size="small"
                                sx={{
                                  bgcolor: alpha(statusInfo.color, 0.1),
                                  color: statusInfo.color,
                                  '& .MuiChip-icon': { color: statusInfo.color },
                                }}
                              />
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {formatDate(email.sent_at || email.created_at)}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        secondary={
                          <React.Fragment>
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{ display: 'block', color: 'text.primary', fontWeight: 500 }}
                            >
                              {email.subject}
                            </Typography>
                            {email.error_message && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ color: 'error.main' }}
                              >
                                Error: {email.error_message}
                              </Typography>
                            )}
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                    <Divider component="li" sx={{ borderColor: 'divider' }} />
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Paper>
      </Box>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Compose Email</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="To"
                type="email"
                value={composeData.to}
                onChange={handleComposeChange('to')}
                required
                placeholder="recipient@example.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={composeData.subject}
                onChange={handleComposeChange('subject')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message (HTML)"
                value={composeData.htmlBody}
                onChange={handleComposeChange('htmlBody')}
                multiline
                rows={10}
                required
                placeholder="<p>Your email content here...</p>"
                helperText="You can use HTML formatting"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Available templates:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {templates.map((template) => (
                  <Chip
                    key={template.id}
                    label={template.name}
                    onClick={() => handleApplyTemplate(template)}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposeOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleSendEmail}
            disabled={!composeData.to || !composeData.subject || !composeData.htmlBody || sending}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Email Dialog */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Email Details</Typography>
            {selectedEmail && (
              <Chip
                icon={getStatusInfo(selectedEmail.status).icon}
                label={selectedEmail.status}
                size="small"
                sx={{
                  bgcolor: alpha(getStatusInfo(selectedEmail.status).color, 0.1),
                  color: getStatusInfo(selectedEmail.status).color,
                }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEmail && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">To</Typography>
                <Typography>{selectedEmail.to_email}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Sent</Typography>
                <Typography>
                  {selectedEmail.sent_at
                    ? new Date(selectedEmail.sent_at).toLocaleString()
                    : 'Not sent'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Subject</Typography>
                <Typography>{selectedEmail.subject}</Typography>
              </Grid>
              {selectedEmail.message_id && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Message ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {selectedEmail.message_id}
                  </Typography>
                </Grid>
              )}
              {selectedEmail.error_message && (
                <Grid item xs={12}>
                  <Alert severity="error">{selectedEmail.error_message}</Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Content</Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}
                >
                  <div dangerouslySetInnerHTML={{ __html: selectedEmail.html_body }} />
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailsPage;